// Homepage funnel for the /ai_manifesto explorer.
//
// A compact prompt strip that sits in the dashboard's main column.
// Reader types a question → submit → navigates to
// /ai_manifesto?q=<question>&mode=ask&lang=en, where ManifestoExplorer
// reads the URL params and auto-fires the query. Single-shot, no
// conversation state held here.
//
// Plain HTML <form> with method="GET" would also work, but we keep an
// onSubmit handler so we can trim + length-cap the question before
// navigating (no need for the explorer to defend against rogue input
// from this entrypoint).

import { useState } from 'react';
import { useFeatureFlags } from '../../lib/useFeatureFlags';
import './ManifestoPromptStrip.css';

const MAX_LEN = 300;

/** Mirrors usePreviewBypass in FeatureSection — `?preview=1` on the
 *  URL or the sticky localStorage bypass. Inlined to avoid a circular
 *  import. */
function isPreviewActive(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const onUrl =
      new URLSearchParams(window.location.search).get('preview') === '1';
    if (onUrl) return true;
    return window.localStorage.getItem('lm-preview-bypass') === '1';
  } catch {
    return false;
  }
}

export default function ManifestoPromptStrip() {
  const [question, setQuestion] = useState('');
  const trimmed = question.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= MAX_LEN;

  // Honour the manifesto-explorer feature flag — same toggle that
  // controls the chat page. Flipping it 'hidden' in /admin/flags
  // takes BOTH surfaces down: the strip on the homepage and the
  // explorer page itself. Brief render-before-flag-load flash is
  // acceptable for the emergency kill path.
  const flags = useFeatureFlags();
  const flag = flags['manifesto-explorer'];
  const flagHidden = !isPreviewActive() && flag?.status === 'hidden';
  if (flagHidden) return null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const target = new URLSearchParams();
    target.set('q', trimmed);
    target.set('lang', 'en');
    // Carry the preview-bypass forward to /ai_manifesto so the
    // explorer's FeatureSection wrapper doesn't hide it when the
    // manifesto-explorer flag is still 'hidden' on this environment.
    // The new page can't depend on localStorage alone — some browsers
    // (Safari ITP, private mode, third-party blockers) drop it across
    // navigations, which is what was making the chat appear and then
    // immediately disappear.
    if (hasPreviewBypass()) target.set('preview', '1');
    window.location.assign(`/ai_manifesto?${target.toString()}`);
  }

  /** True if `?preview=1` is set on the current URL OR was sticky in
   *  localStorage from an earlier visit (the same two sources the
   *  FeatureSection wrapper itself consults). */
  function hasPreviewBypass(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      const onUrl = new URLSearchParams(window.location.search).get('preview') === '1';
      if (onUrl) return true;
      return window.localStorage.getItem('lm-preview-bypass') === '1';
    } catch {
      return false;
    }
  }

  return (
    <section className="mfstrip" aria-labelledby="mfstrip-heading">
      <div className="mfstrip__inner">
        <div className="mfstrip__text">
          <p className="mfstrip__eyebrow">Election 2026 · AI Comparison</p>
          <h2 id="mfstrip-heading" className="mfstrip__title">
            Compare the manifestos
          </h2>
          <p className="mfstrip__sub">
            Ask anything about what each party actually promised. Every answer
            sets PL and PN side by side, and cites the exact manifesto page.
          </p>
        </div>

        <form className="mfstrip__form" onSubmit={onSubmit}>
          <label htmlFor="mfstrip-input" className="visually-hidden">
            Your question
          </label>
          <input
            id="mfstrip-input"
            className="mfstrip__input"
            type="text"
            inputMode="text"
            placeholder="What do you want to know?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={MAX_LEN}
            autoComplete="off"
            spellCheck
          />
          <button
            type="submit"
            className="mfstrip__btn"
            disabled={!canSubmit}
            aria-label="Submit your manifesto comparison question"
          >
            Ask →
          </button>
        </form>

        <p className="mfstrip__hint">
          Beta tool · AI-generated from the PL &amp; PN manifesto PDFs · Verify
          against the cited pages.
        </p>
      </div>
    </section>
  );
}
